import { formatSalesOrderNumber } from '@zspace/format';
import { DeviceGroupsTableRow, SelectedSalesOrderDevices } from '@zspace/types';
import { useCallback } from 'react';
import { formatDateToLocaleString } from '../../shared/utils';
import InputSpinner from '../../ui/input-spinner/input-spinner';
import Link from '../../ui/link/link';
import PaginatedTable, {
  CustomPaginatedTableProps,
} from '../../ui/table/paginated-table/paginated-table';
import { Column } from '../../ui/table/types';

export type QuantitySelection = {
  onChange: (
    selectedSalesOrderId: string,
    selectedSalesOrderNumber: string,
    selectedDeviceModelId: string,
    quantity: number
  ) => void;
  selectedDevices: SelectedSalesOrderDevices[];
};

export type DeviceGroupsTableProps =
  CustomPaginatedTableProps<DeviceGroupsTableRow> & {
    quantitySelection: QuantitySelection;
  };

export function DeviceGroupsTable({
  dataSource,
  onChange,
  config,
  form,
  empty,
  quantitySelection,
  errorMessage,
}: DeviceGroupsTableProps) {
  const columns: Column<DeviceGroupsTableRow>[] = [
    {
      key: 'number',
      className: 'is-width-20',
      render: (el) => (
        <Link to={`/sales-orders/${el.order.id}`}>
          {formatSalesOrderNumber(el.order.number)}
        </Link>
      ),
      title: 'Sales order',
      sortable: true,
    },
    {
      key: 'model_name',
      className: 'is-width-20',
      render: (el) => <p>{el.deviceModel.name}</p>,
      title: 'Device Type',
      sortable: true,
    },
    {
      key: 'purchase',
      className: 'is-width-10',
      render: (el) => formatDateToLocaleString(el.purchaseDate),
      title: 'Purchase date',
      sortable: true,
    },
    {
      key: 'close',
      className: 'is-width-10',
      render: (el) =>
        el.closeDate ? formatDateToLocaleString(el.closeDate) : '',
      title: 'Close date',
      sortable: true,
    },
    {
      key: 'total',
      className: 'is-width-10',
      render: (el) => el.totalDevices,
      title: 'Total',
      sortable: true,
    },
    {
      key: 'available',
      className: 'is-width-10',
      render: (el) => el.totalAvailable,
      title: 'Available',
      sortable: true,
    },
    {
      key: 'selectedQuantity',
      className: 'is-width-20',
      render: (el) => (
        <InputSpinner
          minValue={0}
          maxValue={el.totalAvailable}
          onValueChange={(value) => {
            quantitySelection.onChange(
              el.order.id,
              el.order.number,
              el.deviceModel.id,
              value
            );
          }}
          value={selectedQuantity(el)}
        />
      ),
      title: '',
      sortable: false,
    },
  ];

  const selectedQuantity = useCallback(
    (el: DeviceGroupsTableRow) =>
      quantitySelection.selectedDevices.find(
        (device) =>
          device.salesOrder.id === el.order.id &&
          device.deviceModelId === el.deviceModel.id
      )?.quantity || 0,
    [quantitySelection.selectedDevices]
  );

  return (
    <PaginatedTable
      dataSource={dataSource}
      columns={columns}
      onChange={onChange}
      config={config}
      form={form}
      empty={empty}
      rowKey={'id'}
      errorMessage={errorMessage}
    />
  );
}

export default DeviceGroupsTable;
